import React, { useEffect, useState } from 'react';
import { Box, Chip, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, CircularProgress, Paper } from '@mui/material';
import { Icon } from '@iconify/react';
import DataTable, { Column, RowAction } from './DataTable';
import { useToast } from './ui/ToastProvider';
import { transactionStore, LimitDeactivation } from '../utils/transactionStore';
const CheckerLimitDeactivationTable: React.FC = () => {
  const [requests, setRequests] = useState<LimitDeactivation[]>([]);
  const [selected, setSelected] = useState<LimitDeactivation | null>(null);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState<'approve' | 'reject' | null>(null);
  const [reason, setReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const {
    showToast
  } = useToast();
  const refresh = () => {
    setRequests(transactionStore.getPendingLimitDeactivations());
  };
  useEffect(() => {
    refresh();
    const unsubscribe = transactionStore.subscribe(refresh);
    return unsubscribe;
  }, []);
  const handleAction = (row: LimitDeactivation, action: 'approve' | 'reject') => {
    setSelected(row);
    setActionType(action);
    setActionDialogOpen(true);
  };
  const handleConfirmAction = async () => {
    if (!selected || !actionType) return;
    if (!reason.trim()) return;
    setIsLoading(true);
    try {
      if (actionType === 'approve') {
        transactionStore.approveLimitDeactivation(selected.id, reason);
      } else {
        transactionStore.rejectLimitDeactivation(selected.id, reason);
      }
      showToast(`Limit deactivation ${actionType === 'approve' ? 'approved' : 'rejected'} for "${selected.category}"`, actionType === 'approve' ? 'success' : 'info');
      setActionDialogOpen(false);
      setReason('');
      setSelected(null);
      setActionType(null);
    } finally {
      setIsLoading(false);
    }
  };
  const rowActions: RowAction<LimitDeactivation>[] = [{
    icon: 'lucide:check-circle',
    label: 'Approve',
    onClick: row => handleAction(row, 'approve')
  }, {
    icon: 'lucide:x-circle',
    label: 'Reject',
    onClick: row => handleAction(row, 'reject')
  }];
  const columns: Column<LimitDeactivation>[] = [{
    id: 'id',
    label: 'Request ID',
    renderCell: row => <Typography variant="body2" fontWeight={600} fontFamily="monospace" noWrap data-id="element-637" data-mp="5ky2i25507by">
          {row.id}
        </Typography>
  }, {
    id: 'category',
    label: 'Category',
    renderCell: row => <Typography variant="body2" fontWeight={600} noWrap sx={{
      maxWidth: 260
    }} data-id="element-638" data-mp="ejjgrl3kt06x">
          {row.category}
        </Typography>
  }, {
    id: 'purposeCode',
    label: 'Schedule / Purpose Code',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" color="text.secondary" noWrap data-id="element-639" data-mp="1pz88eprryne2">
          {row.purposeCode}
        </Typography>
  }, {
    id: 'currentLimit',
    label: 'Current Limit',
    renderCell: row => <Typography variant="body2" fontWeight={600} noWrap data-id="element-640" data-mp="3fiy75uwohrqr">
          {row.currency} {row.currentLimit.toLocaleString()}
        </Typography>
  }, {
    id: 'effectiveDate',
    label: 'Effective Date',
    renderCell: row => <Chip label={row.effectiveDate} size="small" sx={{
      fontWeight: 700,
      fontSize: '0.7rem',
      bgcolor: 'rgba(91,78,240,0.1)',
      color: '#5B4EF0'
    }} data-id="element-641" data-mp="30s8tmlvqw0jq" />
  }, {
    id: 'makerReason',
    label: 'Reason',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" noWrap sx={{
      maxWidth: 220
    }} data-id="element-642" data-mp="2rppuabr64628">
          {row.resubmitReason || row.makerReason}
        </Typography>
  }, {
    id: 'requestedBy',
    label: 'Requested By',
    renderCell: row => <Typography variant="body2" fontWeight={500} noWrap data-id="element-643" data-mp="2hvhd2k7l7zjd">
          {row.requestedBy}
        </Typography>
  }, {
    id: 'requestedAt',
    label: 'Requested At',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" noWrap sx={{
      minWidth: 150
    }} data-id="element-644" data-mp="fzxb4kb9uje3">
          {new Date(row.resubmittedAt || row.requestedAt).toLocaleString()}
        </Typography>
  }];
  return <>
      <DataTable title="Pending Limit Deactivation Requests" data={requests} columns={columns} rowActions={rowActions} keyField="id" emptyStateMessage="No pending limit deactivation requests" initialVisibleColumns={['category', 'purposeCode', 'currentLimit', 'effectiveDate', 'makerReason', 'requestedBy', 'requestedAt']} data-id="element-645" data-mp="sz6gut69ul1t" />


      <Dialog open={actionDialogOpen} onClose={() => !isLoading && setActionDialogOpen(false)} maxWidth="sm" fullWidth data-id="element-646" data-mp="3rk7np5tqxj7q">

        <DialogTitle data-id="element-647" data-mp="3iigkc7mohoh1">
          <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }} data-id="element-648" data-mp="3ihygopxos7z3">
            <Icon icon={actionType === 'approve' ? 'lucide:check-circle' : 'lucide:x-circle'} width={24} color={actionType === 'approve' ? '#10B981' : '#EF4444'} data-id="element-649" data-mp="3ikug6ip6n4pn" />

            <Typography variant="h6" fontWeight={600} data-id="element-650" data-mp="2ywoi4rzx5h5u">
              {actionType === 'approve' ? 'Approve' : 'Reject'} Limit Deactivation
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent data-id="element-651" data-mp="37xcvdkhhndho">
          <Box sx={{
          mt: 1
        }} data-id="element-652" data-mp="37x3eqnic2ion">
            {selected && <>
                <Typography variant="body2" color="text.secondary" sx={{
              mb: 2
            }} data-id="element-653" data-mp="3muncb9cwhbks">
                  Category: <strong data-id="element-654" data-mp="3hvk7nq0u17dc">{selected.category}</strong> &middot; Effective Date: <strong data-id="element-655" data-mp="3hv10938t61dk">{selected.effectiveDate}</strong>
                </Typography>

                <Paper variant="outlined" sx={{
              p: 2,
              mb: 3,
              bgcolor: '#F8FAFC'
            }} data-id="element-656" data-mp="3vlnr698bumz3">
                  <Typography variant="caption" color="text.secondary" fontWeight={600} data-id="element-657" data-mp="3rmewn25gql2h">
                    Maker's Reason
                  </Typography>
                  <Typography variant="body2" sx={{
                mt: 0.5
              }} data-id="element-658" data-mp="9deb53kazvlv">
                    {selected.makerReason}
                  </Typography>
                </Paper>

                {selected.resubmitReason && <Paper variant="outlined" sx={{
              p: 2,
              mb: 3,
              bgcolor: 'rgba(245, 158, 11, 0.05)',
              borderColor: '#FCD34D'
            }} data-id="element-659" data-mp="32a7hogenaeu1">

                    <Typography variant="caption" color="#D97706" fontWeight={600} data-id="element-660" data-mp="377r80eomai2c">
                      Previous Rejection Reason
                    </Typography>
                    <Typography variant="body2" sx={{
                mt: 0.5,
                mb: 1.5
              }} data-id="element-661" data-mp="3gzwwvfjkojdb">
                      {selected.checkerComment}
                    </Typography>

                    <Typography variant="caption" color="text.secondary" fontWeight={600} data-id="element-662" data-mp="3m0b3qu9ftn9r">
                      Maker's Resubmit Reason
                    </Typography>
                    <Typography variant="body2" sx={{
                mt: 0.5
              }} data-id="element-663" data-mp="3uxlfegq63ef7">
                      {selected.resubmitReason}
                    </Typography>
                  </Paper>}
              </>}

            <TextField label={`Reason for ${actionType === 'approve' ? 'Approval' : 'Rejection'}`} placeholder={`Enter reason for ${actionType === 'approve' ? 'approving' : 'rejecting'} this deactivation...`} multiline rows={4} fullWidth required value={reason} onChange={e => setReason(e.target.value)} disabled={isLoading} data-id="element-664" data-mp="20l40bwfhfkln" />

          </Box>
        </DialogContent>
        <DialogActions sx={{
        px: 3,
        pb: 2
      }} data-id="element-665" data-mp="2bzfhrftawuju">
          <Button onClick={() => setActionDialogOpen(false)} disabled={isLoading} data-id="element-666" data-mp="271dq46ht7mwr">
            Cancel
          </Button>
          <Button variant="contained" color={actionType === 'approve' ? 'success' : 'error'} onClick={handleConfirmAction} disabled={isLoading || !reason.trim()} startIcon={isLoading ? <CircularProgress size={16} color="inherit" data-id="element-668" data-mp="mlace08togl2" /> : <Icon icon={actionType === 'approve' ? 'lucide:check' : 'lucide:x'} width={16} data-id="element-669" data-mp="mjpi8bqf6znq" />} data-id="element-667" data-mp="220zph9ijff8r">

            {actionType === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </>;
};
export default CheckerLimitDeactivationTable;